import { c as _c } from "react/compiler-runtime";
import { MouseEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { NavigationItemData, NavigationItemWithFlyoutData } from './navigation.types';
import ImageComponent from '@spa/components/image-component/image.component';
import Link from 'next/link';
import classNames from 'clsx';
import logger from '@spa/lib/logger';
import styles from './mobile-navigation-menu.component.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
interface MobileNavigationMenuProps {
  readonly items: ReadonlyArray<NavigationItemWithFlyoutData>;
  readonly onClose: () => void;
  readonly scrollUp: () => void;
}
const PATH_PLACEHOLDER = '/';
type MobileNavigationMenuItemLinkVariant = 'top-level' | 'sub-list' | 'back';
interface MobileNavigationMenuItemLinkProps {
  readonly item: NavigationItemData;
  readonly onLinkClick: (event: MouseEvent<HTMLAnchorElement>, item: NavigationItemData, variant: MobileNavigationMenuItemLinkVariant) => void;
  readonly variant: MobileNavigationMenuItemLinkVariant;
}
interface MobileNavigationMenuListProps {
  readonly isSublistLoaded: (item: NavigationItemData) => boolean;
  readonly isSublistOpen: (item: NavigationItemData) => boolean;
  readonly navigationItems: ReadonlyArray<NavigationItemData>;
  readonly onLinkClick: MobileNavigationMenuItemLinkProps['onLinkClick'];
  readonly parentItem?: NavigationItemData;
  readonly registerSubList: (key: string | undefined, element: HTMLUListElement | null) => void;
}
const filterVisibleMobileItems = <T extends {
  readonly hideMobile: boolean;
},>(items: ReadonlyArray<T>): ReadonlyArray<T> => items.filter(({
  hideMobile
}) => !hideMobile);
const hasFlyout = (item: NavigationItemData | NavigationItemWithFlyoutData): item is NavigationItemWithFlyoutData => (item as unknown as NavigationItemWithFlyoutData).flyout !== undefined;
const getChildren = (item: NavigationItemData | NavigationItemWithFlyoutData): ReadonlyArray<NavigationItemData> => hasFlyout(item) && item.flyout?.categories?.items?.length ? item.flyout.categories.items : item.children ?? [];
const hasChildren = (item: NavigationItemData): boolean => getChildren(item).length > 0;
const getItemKey = (item: Partial<NavigationItemData>): string | undefined => item.id ?? item.path ?? undefined;
const isRenderableItem = (item: NavigationItemData): boolean => Boolean(item.path || hasChildren(item));
const getSublistKeys = (navigationItems: ReadonlyArray<NavigationItemData>): ReadonlyArray<string> => navigationItems.filter(item => getChildren(item).length > 0).map(getItemKey).filter((key): key is string => Boolean(key));
const getVisibleRenderableItems = <T extends NavigationItemData,>(navigationItems: ReadonlyArray<T>): ReadonlyArray<T> => filterVisibleMobileItems(navigationItems).filter(isRenderableItem);
const getTextClassName = (item: NavigationItemData): string => classNames({
  [styles['mobile-navigation-menu__text--highlighted']]: item.highlighted
});
const MobileNavigationMenuItemLink = t0 => {
  const $ = _c(23);
  const {
    item,
    onLinkClick,
    variant
  } = t0;
  const isBackLink = variant === "back";
  let t1;
  if ($[0] !== item) {
    t1 = hasChildren(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const itemHasChildren = t1;
  let t2;
  if ($[2] !== item || $[3] !== onLinkClick || $[4] !== variant) {
    t2 = event => {
      onLinkClick(event, item, variant);
    };
    $[2] = item;
    $[3] = onLinkClick;
    $[4] = variant;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleClick = t2;
  const t3 = item.path ?? PATH_PLACEHOLDER;
  const t4 = styles[variant === "top-level" ? "mobile-navigation-menu__list-item-link" : "mobile-navigation-menu__sub-list-item-link"];
  let t5;
  if ($[6] !== isBackLink || $[7] !== t4) {
    t5 = classNames(t4, {
      [styles["mobile-navigation-menu__list-item-link--back"]]: isBackLink
    });
    $[6] = isBackLink;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== item) {
    t6 = getTextClassName(item);
    $[9] = item;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== item.label || $[12] !== t6) {
    t7 = <span className={t6}>{item.label}</span>;
    $[11] = item.label;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== isBackLink || $[15] !== itemHasChildren) {
    t8 = isBackLink ? <ImageComponent src="/assets/icons/arrow_back.svg" height={16} width={16} alt="" /> : itemHasChildren && <ImageComponent src="/assets/icons/arrow_forward.svg" height={16} width={16} alt="" />;
    $[14] = isBackLink;
    $[15] = itemHasChildren;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== handleClick || $[18] !== t3 || $[19] !== t5 || $[20] !== t7 || $[21] !== t8) {
    t9 = <Link prefetch={false} href={t3} className={t5} onClick={handleClick}>{t7}{t8}</Link>;
    $[17] = handleClick;
    $[18] = t3;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
const MobileNavigationMenuList = t0 => {
  const $ = _c(32);
  const {
    isSublistLoaded,
    isSublistOpen,
    navigationItems,
    onLinkClick,
    parentItem,
    registerSubList
  } = t0;
  let t1;
  if ($[0] !== parentItem) {
    t1 = parentItem ? getItemKey(parentItem) : undefined;
    $[0] = parentItem;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parentKey = t1;
  let t2;
  if ($[2] !== parentKey || $[3] !== registerSubList) {
    t2 = element => {
      registerSubList(parentKey, element);
    };
    $[2] = parentKey;
    $[3] = registerSubList;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = parentItem ? styles["mobile-navigation-menu__sub-list"] : styles["mobile-navigation-menu__list"];
  let t4;
  if ($[5] !== isSublistOpen || $[6] !== parentItem) {
    t4 = parentItem && {
      [styles["mobile-navigation-menu__sub-list--open"]]: isSublistOpen(parentItem),
      [styles["mobile-navigation-menu__sub-list--closed"]]: !isSublistOpen(parentItem)
    };
    $[5] = isSublistOpen;
    $[6] = parentItem;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = classNames(t3, t4);
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== onLinkClick || $[12] !== parentItem) {
    t6 = parentItem && <li className={styles["mobile-navigation-menu__sub-list-item"]}><MobileNavigationMenuItemLink item={parentItem} onLinkClick={onLinkClick} variant="back" /></li>;
    $[11] = onLinkClick;
    $[12] = parentItem;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isSublistLoaded || $[15] !== isSublistOpen || $[16] !== navigationItems || $[17] !== onLinkClick || $[18] !== parentItem || $[19] !== registerSubList) {
    let t8;
    if ($[21] !== isSublistLoaded || $[22] !== isSublistOpen || $[23] !== onLinkClick || $[24] !== parentItem || $[25] !== registerSubList) {
      t8 = item => {
        const children = getChildren(item);
        const itemHasChildren = children.length > 0;
        const shouldRenderSublist = itemHasChildren && isSublistLoaded(item);
        return <li key={getItemKey(item)} className={parentItem ? styles["mobile-navigation-menu__sub-list-item"] : styles["mobile-navigation-menu__list-item"]}><MobileNavigationMenuItemLink item={item} onLinkClick={onLinkClick} variant={parentItem ? "sub-list" : "top-level"} />{shouldRenderSublist && <MobileNavigationMenuList isSublistLoaded={isSublistLoaded} isSublistOpen={isSublistOpen} navigationItems={children} onLinkClick={onLinkClick} parentItem={item} registerSubList={registerSubList} />}</li>;
      };
      $[21] = isSublistLoaded;
      $[22] = isSublistOpen;
      $[23] = onLinkClick;
      $[24] = parentItem;
      $[25] = registerSubList;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    t7 = getVisibleRenderableItems(navigationItems).map(t8);
    $[14] = isSublistLoaded;
    $[15] = isSublistOpen;
    $[16] = navigationItems;
    $[17] = onLinkClick;
    $[18] = parentItem;
    $[19] = registerSubList;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[27] !== t2 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7) {
    t8 = <ul ref={t2} className={t5}>{t6}{t7}</ul>;
    $[27] = t2;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
};
export const MobileNavigationMenu = t0 => {
  const $ = _c(30);
  const {
    items,
    onClose,
    scrollUp
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [openItems, setOpenItems] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [additionalLoadedSublistKeys, setAdditionalLoadedSublistKeys] = useState(t2);
  const [paneHeight, setPaneHeight] = useState();
  const menuPaneRef = useRef(null);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const subListRefs = useRef(t3);
  let t4;
  if ($[3] !== items) {
    t4 = getVisibleRenderableItems(items);
    $[3] = items;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const topLevelItems = t4;
  let t5;
  if ($[5] !== topLevelItems) {
    t5 = new Set(getSublistKeys(topLevelItems));
    $[5] = topLevelItems;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const initiallyLoadedSublistKeys = t5;
  let t6;
  if ($[7] !== additionalLoadedSublistKeys || $[8] !== initiallyLoadedSublistKeys) {
    t6 = item => {
      const key = getItemKey(item);
      return key ? initiallyLoadedSublistKeys.has(key) || additionalLoadedSublistKeys.has(key) : false;
    };
    $[7] = additionalLoadedSublistKeys;
    $[8] = initiallyLoadedSublistKeys;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const isSublistLoaded = t6;
  let t7;
  let t8;
  if ($[10] !== paneHeight) {
    t7 = () => {
      const menuPane = menuPaneRef.current;
      if (!menuPane || paneHeight === undefined) {
        return;
      }
      menuPane.style.setProperty("--menu-pane-height", `${paneHeight}px`);
      return () => {
        menuPane.style.removeProperty("--menu-pane-height");
      };
    };
    t8 = [paneHeight];
    $[10] = paneHeight;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (item_0, isOpen) => {
      const key_0 = getItemKey(item_0);
      if (!key_0) {
        logger.warn(`cannot toggle sublist due to empty key, ${item_0}`);
        return false;
      }
      if (isOpen) {
        const nextDepthSublistKeys = getSublistKeys(getVisibleRenderableItems(getChildren(item_0)));
        setAdditionalLoadedSublistKeys(current => {
          const next = new Set(current).add(key_0);
          nextDepthSublistKeys.forEach(sublistKey => {
            next.add(sublistKey);
          });
          return next;
        });
      }
      setOpenItems(current_0 => new Map(current_0).set(key_0, isOpen));
      return true;
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const setSublistOpen = t9;
  let t10;
  if ($[14] !== openItems) {
    t10 = item_1 => {
      const key_1 = getItemKey(item_1);
      return key_1 ? openItems.get(key_1) ?? false : false;
    };
    $[14] = openItems;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const isSublistOpen = t10;
  let t11;
  if ($[16] !== scrollUp) {
    t11 = (event, item_2) => {
      if (!hasChildren(item_2)) {
        return;
      }
      event.preventDefault();
      if (!setSublistOpen(item_2, true)) {
        return;
      }
      const key_2 = getItemKey(item_2);
      requestAnimationFrame(() => {
        setPaneHeight(key_2 ? subListRefs.current.get(key_2)?.offsetHeight : undefined);
      });
      scrollUp();
    };
    $[16] = scrollUp;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const openSublistOrNavigate = t11;
  let t12;
  if ($[18] !== scrollUp) {
    t12 = (event_0, item_3) => {
      if (!hasChildren(item_3)) {
        return;
      }
      event_0.preventDefault();
      if (!setSublistOpen(item_3, false)) {
        return;
      }
      const currentList = event_0.currentTarget.closest("ul");
      const parentList = currentList?.parentElement?.closest("ul");
      setPaneHeight(parentList?.offsetHeight);
      scrollUp();
    };
    $[18] = scrollUp;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const closeSublist = t12;
  let t13;
  if ($[20] !== closeSublist || $[21] !== onClose || $[22] !== openSublistOrNavigate) {
    t13 = (event_1, item_4, variant) => {
      const isBackLink = variant === "back";
      const itemHasChildren = hasChildren(item_4);
      if (isBackLink) {
        closeSublist(event_1, item_4);
        tracker.navigationClick({
          click_name: item_4.label ?? "unknown",
          click_position: "burger_menu",
          click_url: event_1.currentTarget.href
        });
        return;
      }
      openSublistOrNavigate(event_1, item_4);
      if (!itemHasChildren) {
        onClose();
      }
      tracker.navigationClick({
        click_name: item_4.label ?? "unknown",
        click_position: "burger_menu",
        click_url: itemHasChildren ? undefined : event_1.currentTarget.href
      });
    };
    $[20] = closeSublist;
    $[21] = onClose;
    $[22] = openSublistOrNavigate;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const handleItemLinkClick = t13;
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = (key_3, element) => {
      if (key_3) {
        if (element) {
          subListRefs.current.set(key_3, element);
        } else {
          subListRefs.current.delete(key_3);
        }
      }
    };
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const registerSubList = t14;
  let t15;
  if ($[25] !== handleItemLinkClick || $[26] !== isSublistLoaded || $[27] !== isSublistOpen || $[28] !== topLevelItems) {
    t15 = <div ref={menuPaneRef} className={styles["mobile-navigation-menu__list-wrapper"]}><MobileNavigationMenuList isSublistLoaded={isSublistLoaded} isSublistOpen={isSublistOpen} navigationItems={topLevelItems} onLinkClick={handleItemLinkClick} registerSubList={registerSubList} /></div>;
    $[25] = handleItemLinkClick;
    $[26] = isSublistLoaded;
    $[27] = isSublistOpen;
    $[28] = topLevelItems;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
};